import ConfirmDialog from '@/components/common/ConfirmDialog';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { VOTER_MIN_AGE, voterAgeStatus } from './voterAgeRules';

/** Bengali reads its own numerals, and these forms are printed in Bengali. */
const localizeNumber = (value, language) => (
    String(language || '').startsWith('bn')
        ? String(value).replace(/\d/g, (digit) => '০১২৩৪৫৬৭৮৯'[Number(digit)])
        : String(value)
);

const formatDay = (date, language) => {
    if (!date) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return localizeNumber(`${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`, language);
};

/**
 * Shown when the birth date on an Election Commission form is under 18.
 *
 * It is a warning, not a wall: an NID is issued from 16, the electoral roll
 * starts at 18, and someone may well be filling the form a few weeks before a
 * birthday. So it says what the office is likely to do with the form and lets
 * the applicant carry on.
 *
 * @param {{ open: boolean, birthDate: Date|null, loading?: boolean,
 *   confirmLabel?: string, onCancel: () => void, onConfirm: () => void }} props
 */
export default function UnderageNoticeDialog({
    open,
    birthDate,
    loading = false,
    confirmLabel,
    onCancel,
    onConfirm,
}) {
    const { t, currentLanguage } = useTranslation();
    const status = birthDate ? voterAgeStatus(birthDate) : null;
    if (!status) return null;

    const age = localizeNumber(status.age, currentLanguage);
    const minAge = localizeNumber(VOTER_MIN_AGE, currentLanguage);
    const turns = formatDay(status.turnsVoterAgeOn, currentLanguage);

    return (
        <ConfirmDialog
            open={open}
            onClose={onCancel}
            onConfirm={onConfirm}
            loading={loading}
            variant="primary"
            title={t('underageNotice.title', { age })}
            description={t('underageNotice.description', { minAge })}
            cancelLabel={t('underageNotice.cancel')}
            confirmLabel={confirmLabel || t('underageNotice.confirm')}
        >
            <ul className="w-full space-y-1.5 rounded-xl bg-orange-50/70 px-4 py-3 text-left dark:bg-orange-900/15">
                <li className="text-[12.5px] text-zinc-700 dark:text-zinc-200">
                    • {t('underageNotice.pointVoter')}
                </li>
                {status.turnsVoterAgeOn ? (
                    <li className="text-[12.5px] font-semibold text-orange-600 dark:text-orange-300">
                        • {t('underageNotice.turnsOn', { date: turns })}
                    </li>
                ) : null}
            </ul>
        </ConfirmDialog>
    );
}
